import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, catchError, tap, of, map, switchMap, filter, take, throwError } from 'rxjs';

import { PaginationService } from '../pagination.service';
import { EntityType } from '../../objects/interface-library';
import { Person } from '../../objects/person';

@Injectable({
  providedIn: 'root'
})
export class PeopleService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private allPeopleSubject = new BehaviorSubject<Person[]>([]);
  public allPeople$ = this.allPeopleSubject.asObservable();
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  constructor() {
    this.refreshAllPeopleRecords().subscribe({
      next: () => this.isCacheRefreshedSubject.next(true),
      error: (err) => {
        console.error('Initial people cache refresh failed.', err);
        this.isCacheRefreshedSubject.next(false);
      }
    });
  }

  /**
   * Private function to fetch a single page of people from the API.
   * This is used by the pagination service.
   * @param lastEvaluatedKey The key for the next page of results.
   */
  private getPageOfPeople(lastEvaluatedKey: any = null): Observable<any> {
    const jsonReadyBody = {
      'lastEvaluatedKey': lastEvaluatedKey
    };

    const options = {
      headers: { 'Content-Type': 'application/json' }
    };

    return this.http.post(environment.URL_PEOPLE_GET_PAGE, jsonReadyBody, options).pipe(
      // tap(response => console.log('Fetched people page:', response)),
      catchError(error => {
        console.error('Error fetching people page:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes all people by calling the paginated API and updating the subject.
   */
  public refreshAllPeopleRecords(): Observable<Person[]> {
    return this.paginationService.getAllPaginatedDataProgressively(key => this.getPageOfPeople(key))
      .pipe(
        map(allPeopleRaw => {
          // Adjust this based on your actual response structure
          // If your API returns {contactData: {...}}, use that property name
          return allPeopleRaw
            .filter((person: any) => person.contactData)
            .map((person: any) => person.contactData);
        }),
        // tap(peopleList => console.log('Successfully fetched all people:', peopleList)),
        tap(peopleList => this.allPeopleSubject.next(peopleList)),
        catchError(error => {
          console.error('Error fetching all people:', error);
          this.allPeopleSubject.next([]);
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets a specific person object from the in-memory cache based on their contact number.
   * Waits for the cache to be populated before performing the lookup.
   * @param contactNum The unique contact number of the person.
   * @returns An observable that emits the person object or null if not found.
   */
  public getPersonObject(contactNum: string): Observable<Person | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.allPeople$.pipe(
        map(people => {
          const foundPerson = people.find(person => person.contactNum === contactNum);
          return foundPerson || null;
        }),
        take(1)
      ))
    );
  }

  /**
   * Gets a person's name from the in-memory cache based on their contact number.
   * Waits for the cache to be populated before performing the lookup.
   * @param personNumber The contact number to search for.
   * @returns An observable that emits the person's full name or null if not found.
   */
  public getPersonName(personNumber: string): Observable<string | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.allPeople$.pipe(
        map(people => {
          const foundPerson = people.find(person => person.contactNum === personNumber);
          return foundPerson?.fullName ?? null;
        }),
        take(1)
      ))
    );
  }

  /**
   * Gets a fully-hydrated Person object by their contact number.
   * @param contactNum The contact number of the person to retrieve.
   * @returns An observable of the fully-hydrated Person or null if not found.
   */
  public getPersonFullyHydrated(contactNum: string): Observable<Person | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.getPersonObject(contactNum)),
      switchMap(person => {
        if (!person) {
          return of(null);
        }
        // Future: Add hydration logic here when connections are ready
        // return this.connectionsService.hydrateEntity(person, propertyMap);
        return of(person); // Returning the un-hydrated person for now
      }),
      catchError(error => {
        console.error('Error fetching Person object:', error);
        return of(null);
      })
    );
  }

  /**
   * Writes a Person object to the database.
   * @param person The Person object to write.
   * @returns An observable of the API response.
   */
  public writePerson(person: Person): Observable<any> {
    // Ensure contactNum exists on the person object
    if (!person.contactNum) {
      console.error('Person must have a contactNum');
      return throwError(() => new Error('Person must have a contactNum'));
    }

    // Send the entire person object - the backend will wrap it properly
    const jsonReadyBody = person;

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.put(environment.personWriteUrl, jsonReadyBody, options).pipe(
      // tap(() => console.log('Successfully wrote person to database.')),
      tap(() => {
        // Refresh cache after writing
        this.refreshAllPeopleRecords().subscribe();
      }),
      catchError(error => {
        console.error('An error occurred while writing the person:', error);
        return throwError(() => new Error('Could not write person to the database.'));
      })
    );
  }

  /**
   * Updates a single attribute of a person object in the database.
   * @param contactNum The unique contact number of the person.
   * @param attribute The attribute name to update (e.g., 'attachments', 'criticalDates').
   * @param value The new value for the attribute.
   * @returns An observable of the API response.
   */
  updatePersonAttribute(contactNum: string, attribute: string, value: any): Observable<any> {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    const jsonReadyBody = {
      contactNum,
      attribute,
      value
    };
    return this.http.put(environment.URL_PEOPLE_UPDATE_ATTRIBUTE, jsonReadyBody, options)
      .pipe(
        tap(() => {
          // console.log('Successfully updated person attribute.');
          // Refresh cache after updating
          this.refreshAllPeopleRecords().subscribe();
        }),
        catchError(error => {
          console.error('people.service | An error occurred during attribute update:', error);
          return throwError(() => error);
        })
      );
  }
}